import { createContext, use, useMemo } from 'react';

type ModalContextValue = {
  closeModal: () => void;
};

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

type ModalProviderProps = ModalContextValue & {
  children: React.ReactNode;
};

/**
 * {@link ModalProvider} is a context provider which allows more flexible component composition for
 * our modals by automatically providing a `closeModal` function anywhere it is needed via
 * the {@link useModal} hook.  This means we don't need to explicitly wire up the close callback
 * to the X button each time we use a `ModalHeader`.
 */
export const ModalProvider = ({ closeModal, children }: ModalProviderProps) => {
  const value = useMemo(() => ({ closeModal }), [closeModal]);
  return <ModalContext value={value}>{children}</ModalContext>;
};

/**
 * This hook lets us expose our modal context in a type safe way without needing to supply
 * default arguments to React.createContext, which would be nonsensical.
 *
 * See: https://kentcdodds.com/blog/how-to-use-react-context-effectively#typescript--flow
 */
export function useModal() {
  const context = use(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a Modal component');
  }
  return context;
}
